import React, { useState } from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/utils';

interface Strategy {
  id: string;
  name: string;
  description: string;
  expectedReturn: string;
  riskLevel: string;
  assetGroups: {
    name: string;
    assets: string;
    yieldStrategies: string[];
    share: number;
  }[];
}

const strategies: Strategy[] = [
  {
    id: 'conservative',
    name: 'CONSERVATIVE',
    description: 'CONSERVATIVE',
    expectedReturn: '15%+',
    riskLevel: 'CONSERVATIVE',
    assetGroups: [
      {
        name: 'STABLECOINS',
        assets: 'USDC, USDT, DAI,...',
        yieldStrategies: ['Deposit', 'DEX'],
        share: 100
      }
    ]
  },
  {
    id: 'moderate',
    name: 'MODERATE',
    description: 'MODERATE',
    expectedReturn: '35%+',
    riskLevel: 'MODERATE',
    assetGroups: [
      {
        name: 'STABLECOINS',
        assets: 'USDC, USDT, DAI,...',
        yieldStrategies: ['Deposit', 'DEX'],
        share: 70
      },
      {
        name: 'BLUE_CHIPS',
        assets: 'BTC, ETH, SOL',
        yieldStrategies: ['Staking', 'Deposit', 'DEX'],
        share: 30
      }
    ]
  },
  {
    id: 'balanced',
    name: 'BALANCED',
    description: 'BALANCED',
    expectedReturn: '50%+',
    riskLevel: 'BALANCED',
    assetGroups: [
      {
        name: 'STABLECOINS',
        assets: 'USDC, USDT, DAI,...',
        yieldStrategies: ['Deposit', 'DEX'],
        share: 33
      },
      {
        name: 'BLUE_CHIPS',
        assets: 'BTC, ETH, SOL',
        yieldStrategies: ['Staking', 'Deposit', 'DEX'],
        share: 33
      },
      {
        name: 'TOP_ALTCOINS',
        assets: 'SUI, APT,...',
        yieldStrategies: ['Staking', 'Deposit', 'DEX'],
        share: 33
      }
    ]
  },
  {
    id: 'aggressive',
    name: 'AGGRESSIVE',
    description: 'AGGRESSIVE',
    expectedReturn: '80%+',
    riskLevel: 'AGGRESSIVE',
    assetGroups: [
      {
        name: 'STABLECOINS',
        assets: 'USDC, USDT, DAI,...',
        yieldStrategies: ['Deposit', 'DEX'],
        share: 15
      },
      {
        name: 'BLUE_CHIPS',
        assets: 'BTC, ETH, SOL',
        yieldStrategies: ['Staking', 'Deposit', 'DEX'],
        share: 65
      },
      {
        name: 'TOP_ALTCOINS',
        assets: 'SUI, APT,...',
        yieldStrategies: ['Staking', 'Deposit', 'DEX'],
        share: 8
      },
      {
        name: 'OTHER_ALTCOINS',
        assets: 'DePIN, DeFi, memes, AI,...',
        yieldStrategies: ['Staking', 'Deposit', 'DEX'],
        share: 6
      },
      {
        name: 'INDICES',
        assets: 'DePIN Index, Meme Index,...',
        yieldStrategies: ['-'],
        share: 6
      }
    ]
  }
];

const colors = ['#4A9DC7', '#FF6B00', '#1E40AF', '#10B981', '#8B5CF6', '#EC4899'];

const PortfolioStrategyScreen: React.FC = () => {
  const [expandedGroups, setExpandedGroups] = useState<{[key: string]: boolean}>({});
  const { t } = useTranslation();

  const toggleGroupDetails = (strategyId: string, groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [`${strategyId}-${groupName}`]: !prev[`${strategyId}-${groupName}`]
    }));
  };

  return (
    <div className="flex flex-col gap-6 bg-tg-bg">
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-tg-text m-0">{t('PORTFOLIO_STRATEGY_SCREEN.TITLE')}</h1>
        <p className="text-[15px] text-tg-hint m-0">{t('PORTFOLIO_STRATEGY_SCREEN.SUBTITLE')}</p>
      </div>

      <div className="flex flex-col gap-4">
        {strategies.map((strategy) => (
          <div
            key={strategy.id}
            className="bg-tg-bg rounded-[20px] p-6 flex flex-col gap-6 relative overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.08)]"
            style={{
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 0 0 1px rgba(255, 0, 128, 0.1)'
            }}
          >
            {/* Градиентный фон */}
            <div 
              className="absolute inset-0 z-[1] pointer-events-none opacity-50"
              style={{
                background: 'radial-gradient(circle at top left, rgba(255, 0, 128, 0.08), transparent 50%), radial-gradient(circle at bottom right, rgba(255, 107, 0, 0.08), transparent 50%)'
              }}
            />

            <div className="flex justify-between items-start mb-4 relative z-[2] gap-3 flex-wrap">
              <h2 
                className="text-[22px] font-semibold text-tg-text m-0 flex-shrink min-w-0 whitespace-nowrap overflow-hidden text-ellipsis max-[360px]:text-xl"
                style={{
                  background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                  lineHeight: '1.2'
                }}
              >
                {t(`PORTFOLIO_STRATEGY_SCREEN.STRATEGIES.${strategy.name}.NAME`)}
              </h2>
              <span 
                className={cn(
                  'px-3 py-1.5 rounded-xl text-[13px] font-medium whitespace-nowrap flex-shrink-0 max-[360px]:px-2.5 max-[360px]:py-1 max-[360px]:text-xs',
                  strategy.id === 'conservative' && 'bg-[rgba(74,157,199,0.1)] text-[#4A9DC7]',
                  strategy.id === 'moderate' && 'bg-[rgba(255,107,0,0.1)] text-[#FF6B00]',
                  strategy.id === 'aggressive' && 'bg-[rgba(220,38,38,0.15)] text-[#DC2626]',
                  strategy.id === 'balanced' && 'bg-gradient-pink text-white shadow-[0_4px_12px_rgba(255,0,128,0.2)]'
                )}
                style={{
                  letterSpacing: strategy.id !== 'balanced' ? '-0.01em' : undefined
                }}
              >
                {t(`PORTFOLIO_STRATEGY_SCREEN.STRATEGIES.${strategy.name}.RISK_LEVEL`)}
              </span>
            </div>

            <div className="w-[200px] h-[200px] mx-auto mb-5 relative z-[2]">
              <PieChart
                data={strategy.assetGroups.map((group, index) => ({
                  title: t(`PORTFOLIO_STRATEGY_SCREEN.ASSET_GROUPS.${group.name}`),
                  value: group.share,
                  color: colors[index]
                }))}
                lineWidth={20}
                paddingAngle={2}
                rounded
              />
            </div>

            <div className="flex flex-col gap-3 mb-5 relative z-[2]">
              {strategy.assetGroups.map((group, index) => (
                <div key={group.name}>
                  <div 
                    className={cn(
                      'flex items-center gap-2 bg-tg-secondary-bg px-4 py-3 rounded-2xl border border-pink-500/10 cursor-pointer transition-all duration-300 relative overflow-hidden',
                      'hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(255,0,128,0.1)] active:translate-y-0',
                      expandedGroups[`${strategy.id}-${group.name}`] && 'bg-pink-500/5 border-pink-500/20'
                    )}
                    onClick={() => toggleGroupDetails(strategy.id, group.name)}
                  >
                    {/* Градиентный эффект при hover */}
                    <div 
                      className="absolute inset-0 opacity-0 transition-opacity duration-300 pointer-events-none"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255, 0, 128, 0.1), rgba(255, 107, 0, 0.1))'
                      }}
                    />
                    
                    <span 
                      className="w-3 h-3 rounded-sm transition-transform duration-300 relative z-[1]"
                      style={{ 
                        background: colors[index],
                        transform: expandedGroups[`${strategy.id}-${group.name}`] ? 'scale(1.1)' : undefined
                      }} 
                    />
                    <span className="flex-1 text-sm text-tg-hint transition-colors duration-300 relative z-[1] hover:text-tg-text">
                      {t(`PORTFOLIO_STRATEGY_SCREEN.ASSET_GROUPS.${group.name}`)}
                    </span>
                    <span 
                      className="text-sm font-semibold mx-2 relative z-[1] transition-opacity duration-300"
                      style={{
                        background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      {group.share}%
                    </span>
                    <ChevronDown 
                      className={cn(
                        'text-tg-hint transition-all duration-300 relative z-[1]',
                        'hover:text-[#FF0080]',
                        expandedGroups[`${strategy.id}-${group.name}`] && 'rotate-180 text-[#FF0080]'
                      )}
                      size={16} 
                    />
                  </div>
                  {expandedGroups[`${strategy.id}-${group.name}`] && (
                    <div 
                      className="mt-1 mb-3 px-5 py-4 bg-tg-secondary-bg rounded-2xl border border-pink-500/10 relative"
                      style={{
                        animation: 'slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03), inset 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      {/* Стрелка вверх */}
                      <div 
                        className="absolute -top-1.5 left-6 w-3 h-3 bg-tg-secondary-bg border-l border-t border-pink-500/10 rotate-45"
                      />
                      
                      <div 
                        className="flex justify-between items-center py-2.5"
                        style={{
                          animation: 'fadeIn 0.3s ease-out forwards',
                          animationDelay: '0.1s',
                          opacity: 0
                        }}
                      >
                        <span className="text-[13px] text-tg-hint font-medium">
                          {t('PORTFOLIO_STRATEGY_SCREEN.DETAILS.ASSETS')}:
                        </span>
                        <span 
                          className="text-[13px] font-semibold text-tg-text"
                          style={{
                            background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}
                        >
                          {group.assets}
                        </span>
                      </div>
                      <div 
                        className="flex justify-between items-center py-2.5 border-t border-pink-500/10"
                        style={{
                          animation: 'fadeIn 0.3s ease-out forwards',
                          animationDelay: '0.1s',
                          opacity: 0
                        }}
                      >
                        <span className="text-[13px] text-tg-hint font-medium">
                          {t('PORTFOLIO_STRATEGY_SCREEN.DETAILS.YIELD_STRATEGY')}:
                        </span>
                        <span 
                          className="text-[13px] font-semibold text-tg-text"
                          style={{
                            background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}
                        >
                          {group.yieldStrategies.join(', ')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <p className="text-sm leading-6 text-tg-hint -mt-4 mb-0 relative z-[2]">
              {t(`PORTFOLIO_STRATEGY_SCREEN.STRATEGIES.${strategy.name}.DESCRIPTION`)}
            </p>

            <div className="flex justify-between items-center p-4 bg-tg-secondary-bg rounded-xl border border-pink-500/10 relative z-[2]">
              <span className="text-sm text-tg-hint">
                {t('PORTFOLIO_STRATEGY_SCREEN.DETAILS.APY_PROJECTION')}:
              </span>
              <span 
                className="text-xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #FF0080, #FF6B00)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {strategy.expectedReturn}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PortfolioStrategyScreen; 